import React, { useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Settings, X } from 'lucide-react';
import { Solar } from 'lunar-typescript';
import XuanYuanTopBar from './XuanYuanTopBar';

type Wuxing = '金' | '木' | '水' | '火' | '土';

type GradeKey = 'noble' | 'lucky' | 'wealth' | 'tiring' | 'avoid';

interface GradeConfig {
  key: GradeKey;
  title: string;
  relation: string;
  desc: string;
  badgeClass: string;
  cardClass: string;
  titleClass: string;
}
type WuxingTheme = 'warm' | 'modern';
const WUXING_THEME_STORAGE_KEY = 'qimen_wuxing_clothing_theme_v1';

const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;

const BRANCH_WUXING: Record<(typeof BRANCHES)[number], Wuxing> = {
  子: '水',
  丑: '土',
  寅: '木',
  卯: '木',
  辰: '土',
  巳: '火',
  午: '火',
  未: '土',
  申: '金',
  酉: '金',
  戌: '土',
  亥: '水',
};

const WUXING_COLORS: Record<Wuxing, string[]> = {
  金: ['白色', '银色'],
  木: ['绿色'],
  水: ['黑色', '深蓝色'],
  火: ['红色', '粉红色'],
  土: ['黄色', '咖啡色', '泥土色'],
};

const WUXING_GENERATES: Record<Wuxing, Wuxing> = {
  木: '火',
  火: '土',
  土: '金',
  金: '水',
  水: '木',
};

const WUXING_CONTROLS: Record<Wuxing, Wuxing> = {
  木: '土',
  土: '水',
  水: '火',
  火: '金',
  金: '木',
};

const BRANCH_ZODIAC: Record<(typeof BRANCHES)[number], string> = {
  子: '鼠',
  丑: '牛',
  寅: '虎',
  卯: '兔',
  辰: '龙',
  巳: '蛇',
  午: '马',
  未: '羊',
  申: '猴',
  酉: '鸡',
  戌: '狗',
  亥: '猪',
};

const BRANCH_OPPOSITE: Record<(typeof BRANCHES)[number], (typeof BRANCHES)[number]> = {
  子: '午',
  丑: '未',
  寅: '申',
  卯: '酉',
  辰: '戌',
  巳: '亥',
  午: '子',
  未: '丑',
  申: '寅',
  酉: '卯',
  戌: '辰',
  亥: '巳',
};

const DIRECTION_BY_STEM: Record<(typeof STEMS)[number], { yinNoble: string; yangNoble: string; wealth: string; joy: string; fortune: string }> = {
  甲: { yinNoble: '东北', yangNoble: '西南', wealth: '东北', joy: '东北', fortune: '正北' },
  乙: { yinNoble: '正南', yangNoble: '正西', wealth: '东北', joy: '西北', fortune: '西南' },
  丙: { yinNoble: '西北', yangNoble: '正西', wealth: '西南', joy: '西南', fortune: '西北' },
  丁: { yinNoble: '正西', yangNoble: '西北', wealth: '西南', joy: '正南', fortune: '正东' },
  戊: { yinNoble: '西南', yangNoble: '东北', wealth: '正北', joy: '东南', fortune: '东北' },
  己: { yinNoble: '西南', yangNoble: '正北', wealth: '正北', joy: '东北', fortune: '正南' },
  庚: { yinNoble: '西南', yangNoble: '东北', wealth: '正东', joy: '西北', fortune: '西北' },
  辛: { yinNoble: '正南', yangNoble: '正东', wealth: '正东', joy: '西南', fortune: '西南' },
  壬: { yinNoble: '东南', yangNoble: '正东', wealth: '正南', joy: '正南', fortune: '东北' },
  癸: { yinNoble: '正东', yangNoble: '东南', wealth: '正南', joy: '东南', fortune: '正北' },
};

const WARM_GRADE_CONFIG: GradeConfig[] = [
  {
    key: 'noble',
    title: '贵人色',
    relation: '天生我',
    desc: '五行相生，与大环境磁场合一圆融，办事遇贵人，易获扶助，异性缘比平日增加，开心轻松。',
    badgeClass: 'bg-gradient-to-br from-[#3C9D66] to-[#2C7A4A] text-white',
    cardClass: 'border-[#D9C2AB] bg-gradient-to-r from-[#FFF3E5] to-[#FFF8F0] shadow-[0_6px_12px_rgba(173,111,79,0.12)]',
    titleClass: 'text-[#85532F]',
  },
  {
    key: 'lucky',
    title: '幸运色',
    relation: '天同我',
    desc: '五行相符，加持好运，与人合作合伙共分利益，商务沟通、谈判等开心轻松，实现共赢。',
    badgeClass: 'bg-gradient-to-br from-[#4EA567] to-[#3D8552] text-white',
    cardClass: 'border-[#E2CDB8] bg-gradient-to-r from-[#FFF2DF] to-[#FFF7EE] shadow-[0_6px_12px_rgba(173,111,79,0.1)]',
    titleClass: 'text-[#8D5C35]',
  },
  {
    key: 'wealth',
    title: '招财色',
    relation: '我克天',
    desc: '五行相克，你克大环境，克者为财，有财进账。但需要付出努力，如果成功，收获较大。',
    badgeClass: 'bg-gradient-to-br from-[#C65A4D] to-[#D27F47] text-white',
    cardClass: 'border-[#E6C7AD] bg-gradient-to-r from-[#FFEFE2] to-[#FFF7ED] shadow-[0_6px_12px_rgba(198,90,77,0.12)]',
    titleClass: 'text-[#985634]',
  },
  {
    key: 'tiring',
    title: '劳累色',
    relation: '我生天',
    desc: '你要去生大环境，万事会较累，易感疲劳，力不从心，但也有收获。适合内心强大的你去挑战。',
    badgeClass: 'bg-gradient-to-br from-[#6076BF] to-[#4F63A6] text-white',
    cardClass: 'border-[#DBC9B8] bg-gradient-to-r from-[#FFF4E9] to-[#FFF8F1] shadow-[0_6px_12px_rgba(114,99,160,0.12)]',
    titleClass: 'text-[#7F5535]',
  },
  {
    key: 'avoid',
    title: '相克色',
    relation: '天克我',
    desc: '大环境克你，受大环境制约，万事成效差，易导致运势低下，且易事倍功半，徒劳无功。',
    badgeClass: 'bg-gradient-to-br from-[#B34747] to-[#CD6148] text-white',
    cardClass: 'border-[#E4C4B8] bg-gradient-to-r from-[#FFECE7] to-[#FFF6EE] shadow-[0_6px_12px_rgba(185,71,71,0.12)]',
    titleClass: 'text-[#9A4A3A]',
  },
];

const MODERN_GRADE_CONFIG: GradeConfig[] = [
  {
    key: 'noble',
    title: '贵人色',
    relation: '天生我',
    desc: '五行相生，与大环境磁场合一圆融，办事遇贵人，易获扶助，异性缘比平日增加，开心轻松。',
    badgeClass: 'bg-gradient-to-br from-rose-500 to-red-600 text-white',
    cardClass: 'border-rose-300 bg-gradient-to-r from-rose-50 to-red-50 shadow-[0_6px_14px_rgba(244,63,94,0.14)]',
    titleClass: 'text-rose-700',
  },
  {
    key: 'lucky',
    title: '幸运色',
    relation: '天同我',
    desc: '五行相符，加持好运，与人合作合伙共分利益，商务沟通、谈判等开心轻松，实现共赢。',
    badgeClass: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white',
    cardClass: 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-[0_6px_14px_rgba(16,185,129,0.14)]',
    titleClass: 'text-emerald-700',
  },
  {
    key: 'wealth',
    title: '招财色',
    relation: '我克天',
    desc: '五行相克，你克大环境，克者为财，有财进账。但需要付出努力，如果成功，收获较大。',
    badgeClass: 'bg-gradient-to-br from-amber-500 to-orange-600 text-white',
    cardClass: 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-[0_6px_14px_rgba(245,158,11,0.14)]',
    titleClass: 'text-amber-700',
  },
  {
    key: 'tiring',
    title: '劳累色',
    relation: '我生天',
    desc: '你要去生大环境，万事会较累，易感疲劳，力不从心，但也有收获。适合内心强大的你去挑战。',
    badgeClass: 'bg-gradient-to-br from-sky-500 to-blue-600 text-white',
    cardClass: 'border-sky-300 bg-gradient-to-r from-sky-50 to-blue-50 shadow-[0_6px_14px_rgba(59,130,246,0.12)]',
    titleClass: 'text-sky-700',
  },
  {
    key: 'avoid',
    title: '相克色',
    relation: '天克我',
    desc: '大环境克你，受大环境制约，万事成效差，易导致运势低下，且易事倍功半，徒劳无功。',
    badgeClass: 'bg-gradient-to-br from-red-600 to-orange-700 text-white',
    cardClass: 'border-red-300 bg-gradient-to-r from-red-50 to-orange-50 shadow-[0_6px_14px_rgba(239,68,68,0.12)]',
    titleClass: 'text-red-700',
  },
];

const THEME_ELEMENT_STYLE: Record<WuxingTheme, Record<Wuxing, {
  titleColor: string;
  borderColor: string;
  cardBg: string;
  badgeBg: string;
  badgeTextColor: string;
  shadow: string;
}>> = {
  warm: {
    金: {
      titleColor: '#7F8A95',
      borderColor: '#D2D8DF',
      cardBg: 'linear-gradient(90deg, #FCFDFE 0%, #F5F8FB 100%)',
      badgeBg: 'linear-gradient(135deg, #EEF2F7 0%, #D6DEE8 100%)',
      badgeTextColor: '#334155',
      shadow: '0 6px 14px rgba(71, 85, 105, 0.10)',
    },
    木: {
      titleColor: '#2E7D46',
      borderColor: '#A4D2B3',
      cardBg: 'linear-gradient(90deg, #F3FBEF 0%, #F8FFF5 100%)',
      badgeBg: 'linear-gradient(135deg, #249A47 0%, #18753D 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(36, 154, 71, 0.12)',
    },
    水: {
      titleColor: '#1F4DA5',
      borderColor: '#96B5E1',
      cardBg: 'linear-gradient(90deg, #EFF6FF 0%, #F6FAFF 100%)',
      badgeBg: 'linear-gradient(135deg, #2B63C8 0%, #1E3E91 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(43, 99, 200, 0.12)',
    },
    火: {
      titleColor: '#C5484F',
      borderColor: '#E9B0B7',
      cardBg: 'linear-gradient(90deg, #FFF0F2 0%, #FFF7F8 100%)',
      badgeBg: 'linear-gradient(135deg, #EB4E58 0%, #D33C7A 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(211, 60, 122, 0.12)',
    },
    土: {
      titleColor: '#8A5A2F',
      borderColor: '#D9B791',
      cardBg: 'linear-gradient(90deg, #FFF4E8 0%, #FFF9F1 100%)',
      badgeBg: 'linear-gradient(135deg, #B27A42 0%, #8E5E31 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(178, 122, 66, 0.12)',
    },
  },
  modern: {
    金: {
      titleColor: '#6B7280',
      borderColor: '#CBD5E1',
      cardBg: 'linear-gradient(90deg, #F8FAFC 0%, #F1F5F9 100%)',
      badgeBg: 'linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)',
      badgeTextColor: '#334155',
      shadow: '0 6px 14px rgba(100, 116, 139, 0.12)',
    },
    木: {
      titleColor: '#047857',
      borderColor: '#6EE7B7',
      cardBg: 'linear-gradient(90deg, #ECFDF5 0%, #F0FDF4 100%)',
      badgeBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(16, 185, 129, 0.14)',
    },
    水: {
      titleColor: '#1D4ED8',
      borderColor: '#93C5FD',
      cardBg: 'linear-gradient(90deg, #EFF6FF 0%, #F0F9FF 100%)',
      badgeBg: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(37, 99, 235, 0.14)',
    },
    火: {
      titleColor: '#DC2626',
      borderColor: '#FDA4AF',
      cardBg: 'linear-gradient(90deg, #FFF1F2 0%, #FFEFF6 100%)',
      badgeBg: 'linear-gradient(135deg, #EF4444 0%, #EC4899 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(244, 63, 94, 0.14)',
    },
    土: {
      titleColor: '#B45309',
      borderColor: '#FCD34D',
      cardBg: 'linear-gradient(90deg, #FFFBEB 0%, #FFF7ED 100%)',
      badgeBg: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      badgeTextColor: '#FFFFFF',
      shadow: '0 6px 14px rgba(217, 119, 6, 0.14)',
    },
  },
};

const RELATION_ORDER: GradeKey[] = ['noble', 'lucky', 'wealth', 'tiring', 'avoid'];

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d: Date, days: number): Date => {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
};

const normalizeWuxingTheme = (raw: unknown): WuxingTheme => (
  raw === 'warm' ? 'warm' : 'modern'
);

const loadStoredTheme = (): WuxingTheme => {
  try {
    return normalizeWuxingTheme(localStorage.getItem(WUXING_THEME_STORAGE_KEY));
  } catch {
    return 'modern';
  }
};

const saveStoredTheme = (theme: WuxingTheme): void => {
  try {
    localStorage.setItem(WUXING_THEME_STORAGE_KEY, normalizeWuxingTheme(theme));
  } catch {
    // ignore
  }
};

const getRelation = (me: Wuxing, env: Wuxing): GradeKey => {
  if (WUXING_GENERATES[env] === me) return 'noble';
  if (me === env) return 'lucky';
  if (WUXING_CONTROLS[me] === env) return 'wealth';
  if (WUXING_GENERATES[me] === env) return 'tiring';
  return 'avoid';
};

const resolveGradeElements = (env: Wuxing): Record<GradeKey, Wuxing> => {
  const all: Wuxing[] = ['金', '木', '水', '火', '土'];
  const map = {} as Record<GradeKey, Wuxing>;
  for (const me of all) {
    const key = getRelation(me, env);
    map[key] = me;
  }
  return map;
};

const parseGanZhi = (ganZhi: string): { stem: (typeof STEMS)[number]; branch: (typeof BRANCHES)[number] } => {
  const stem = (STEMS.find((s) => ganZhi.includes(s)) || '甲') as (typeof STEMS)[number];
  const branch = (BRANCHES.find((z) => ganZhi.includes(z)) || '子') as (typeof BRANCHES)[number];
  return { stem, branch };
};

interface WuxingClothingProps {
  onBack: () => void;
  theme?: WuxingTheme;
  onThemeChange?: (theme: WuxingTheme) => void;
}

const WuxingClothing: React.FC<WuxingClothingProps> = ({ onBack, theme, onThemeChange }) => {
  const [date, setDate] = useState<Date>(new Date());
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [internalTheme, setInternalTheme] = useState<WuxingTheme>(() => theme ?? loadStoredTheme());
  const activeTheme = theme ?? internalTheme;

  const data = useMemo(() => {
    const solar = Solar.fromYmd(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const lunar = solar.getLunar();

    const yearGz = lunar.getYearInGanZhiExact();
    const monthGz = lunar.getMonthInGanZhiExact();
    const dayGz = lunar.getDayInGanZhiExact();
    const weekCn = lunar.getWeekInChinese();
    const lunarText = `${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}`;

    const { stem: dayStem, branch: dayBranch } = parseGanZhi(dayGz);
    const env = BRANCH_WUXING[dayBranch];
    const gradeElements = resolveGradeElements(env);
    const zodiacGood = BRANCH_ZODIAC[dayBranch];
    const zodiacBad = BRANCH_ZODIAC[BRANCH_OPPOSITE[dayBranch]];
    const dirs = DIRECTION_BY_STEM[dayStem];

    const grades = RELATION_ORDER.map((key) => {
      const source = activeTheme === 'modern' ? MODERN_GRADE_CONFIG : WARM_GRADE_CONFIG;
      const cfg = source.find((x) => x.key === key)!;
      const elem = gradeElements[key];
      return {
        ...cfg,
        element: elem,
        colors: WUXING_COLORS[elem],
      };
    });

    return {
      yearGz,
      monthGz,
      dayGz,
      weekCn,
      lunarText,
      dayBranch,
      zodiacGood,
      zodiacBad,
      dirs,
      grades,
    };
  }, [date, activeTheme]);

  const yiColors = data.grades[0].colors.join('、');
  const jiColors = data.grades[data.grades.length - 1].colors.join('、');
  const levelText = ['穿衣大吉', '穿衣次吉', '穿衣平吉', '穿衣一般', '穿衣大忌'];
  const renderLevelText = (text: string): string => {
    const clean = String(text || '').trim();
    if (clean.length === 4) return `${clean.slice(0, 2)}\n${clean.slice(2)}`;
    return clean;
  };

  const isModern = activeTheme === 'modern';
  const applyTheme = (nextTheme: WuxingTheme) => {
    setInternalTheme(nextTheme);
    saveStoredTheme(nextTheme);
    onThemeChange?.(nextTheme);
    setShowThemeSettings(false);
  };

  return (
    <div className={`${isModern ? 'min-h-full bg-gradient-to-b from-[#f2f9ff] via-[#f7fff6] to-[#fff7fb]' : 'min-h-full bg-[#FAF8F2]'} text-slate-900 pb-3`}>
      <XuanYuanTopBar
        title="轩辕国学·五行穿衣"
        onBack={onBack}
        variant="lightBlend"
        maxWidthClassName="max-w-xl"
      />

      <div className="mx-auto max-w-xl px-2.5 pt-2">
        <div className="grid grid-cols-[74px_1fr_74px] items-center mb-0.5">
          <div className="w-[74px]" />
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => setDate((d) => addDays(d, -1))} className="p-1 rounded-full hover:bg-slate-200/60" aria-label="前一天">
              <ChevronLeft className="w-4 h-4 text-slate-900" />
            </button>
            <div className="text-[26px] sm:text-[28px] font-semibold leading-none tracking-[0.01em]">{data.lunarText}</div>
            <button onClick={() => setDate((d) => addDays(d, 1))} className="p-1 rounded-full hover:bg-slate-200/60" aria-label="后一天">
              <ChevronRight className="w-4 h-4 text-slate-900" />
            </button>
          </div>
          <div className="flex items-center justify-end gap-0.5">
            <label className="p-1.5 rounded-full hover:bg-slate-200/60 cursor-pointer" aria-label="选择日期">
              <CalendarDays className="w-4 h-4 text-slate-800" />
              <input
                className="sr-only"
                type="date"
                value={formatDate(date)}
                onChange={(e) => {
                  if (!e.target.value) return;
                  const [y, m, d] = e.target.value.split('-').map((x) => parseInt(x, 10));
                  setDate(new Date(y, (m || 1) - 1, d || 1));
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => setShowThemeSettings(true)}
              className="p-1.5 rounded-full hover:bg-slate-200/60"
              aria-label="主题设置"
            >
              <Settings className="w-4 h-4 text-slate-800" />
            </button>
          </div>
        </div>

        <div className="text-center text-[14px] sm:text-[15px] text-slate-600 font-medium mb-0.5">
          {date.getFullYear()}年{date.getMonth() + 1}月{date.getDate()}日
        </div>

        <div className="text-center text-[13px] sm:text-[14px] tracking-wide text-[#3F3128] font-semibold">
          {data.yearGz}年 {data.monthGz}月 {data.dayGz}日冲
          <span className="text-red-600">{data.zodiacBad}</span>
          {' '}星期{data.weekCn}
        </div>

        <div className="mt-2 space-y-1">
          <div className={`${isModern ? 'rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-cyan-50 shadow-[0_8px_14px_rgba(16,185,129,0.10)]' : 'rounded-xl border border-[#E2D2BF] bg-gradient-to-r from-[#FFF3E8] to-[#FFF9F1] shadow-[0_8px_14px_rgba(148,87,67,0.08)]'} px-2 py-1.5 text-[14px] sm:text-[15px] font-semibold flex items-center gap-1.5`}>
            <span className={`inline-flex items-center justify-center min-w-7 h-7 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white text-[12px]`}>宜</span>
            <span>{yiColors}</span>
          </div>
          <div className={`${isModern ? 'rounded-xl border border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50 shadow-[0_8px_14px_rgba(239,68,68,0.10)]' : 'rounded-xl border border-[#E5D3C8] bg-gradient-to-r from-[#FFF0EA] to-[#FFF8F0] shadow-[0_8px_14px_rgba(148,87,67,0.07)]'} px-2 py-1.5 text-[14px] sm:text-[15px] font-semibold flex items-center gap-1.5`}>
            <span className={`inline-flex items-center justify-center min-w-7 h-7 rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white text-[12px]`}>忌</span>
            <span>{jiColors}</span>
          </div>
        </div>

        <div className={`${isModern ? 'mt-2 rounded-2xl border border-sky-100 bg-white/90 shadow-[0_10px_20px_rgba(59,130,246,0.08)]' : 'mt-2 rounded-2xl border border-[#E8DFD1] bg-[#FFFDF9] shadow-[0_10px_22px_rgba(148,87,67,0.08)]'} p-1.5`}>
          <div className={`grid grid-cols-5 text-center bg-white rounded-xl border ${isModern ? 'border-sky-100' : 'border-[#E8DFD1]'} overflow-hidden`}>
            {[
              { label: '阴贵人', value: data.dirs.yinNoble },
              { label: '阳贵人', value: data.dirs.yangNoble },
              { label: '财神', value: data.dirs.wealth },
              { label: '喜神', value: data.dirs.joy },
              { label: '福神', value: data.dirs.fortune },
            ].map((x) => (
              <div key={x.label} className={`py-1 border-r ${isModern ? 'border-sky-100' : 'border-[#EFE5D8]'} last:border-r-0`}>
                <div className={`text-[12px] sm:text-[13px] font-medium ${isModern ? 'text-sky-700/80' : 'text-[#9B8876]'}`}>{x.label}</div>
                <div className={`text-[16px] sm:text-[17px] leading-tight font-bold ${isModern ? 'text-sky-800' : 'text-[#7A4E31]'}`}>{x.value}</div>
              </div>
            ))}
          </div>

          <div className={`mt-1.5 rounded-xl border ${isModern ? 'border-sky-100 bg-gradient-to-r from-white to-sky-50 shadow-[0_6px_14px_rgba(59,130,246,0.07)]' : 'border-[#E8DFD1] bg-gradient-to-r from-[#FFF8EE] to-[#FFFDF9] shadow-[0_6px_14px_rgba(148,87,67,0.06)]'} px-2 py-1.5 text-center`}>
            <div className={`text-[14px] sm:text-[15px] font-semibold ${isModern ? 'text-sky-700' : 'text-[#7A4E31]'}`}>《今日建议》</div>
            <div className={`text-[12px] sm:text-[13px] mt-0.5 leading-[1.45] ${isModern ? 'text-slate-600' : 'text-[#5A4535]'}`}>
              时日属{data.zodiacGood}大吉，属{data.zodiacBad}诸事不宜，行事需谨慎
            </div>
          </div>

          <div className="mt-2 space-y-1.5">
            {data.grades.map((g, idx) => (
              <div
                key={g.key}
                className="rounded-xl border px-1.5 py-1 flex gap-1.5 min-h-[68px]"
                style={{
                  borderColor: THEME_ELEMENT_STYLE[activeTheme][g.element].borderColor,
                  background: THEME_ELEMENT_STYLE[activeTheme][g.element].cardBg,
                  boxShadow: THEME_ELEMENT_STYLE[activeTheme][g.element].shadow,
                }}
              >
                <div
                  className="h-[58px] w-[58px] sm:h-[60px] sm:w-[60px] self-center shrink-0 rounded-xl flex items-center justify-center whitespace-pre-line text-center text-[12px] sm:text-[13px] leading-tight font-bold px-1"
                  style={{
                    background: THEME_ELEMENT_STYLE[activeTheme][g.element].badgeBg,
                    color: THEME_ELEMENT_STYLE[activeTheme][g.element].badgeTextColor,
                  }}
                >
                  {renderLevelText(levelText[idx])}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] sm:text-[14px] font-semibold leading-snug break-words">
                    <span style={{ color: THEME_ELEMENT_STYLE[activeTheme][g.element].titleColor }}>{g.title}</span>
                    ：<span className="text-slate-900 break-all">{g.colors.join('、')}</span>
                  </div>
                  <div className="mt-0.5 min-h-[2.2em] text-[11px] sm:text-[12px] text-slate-600 leading-[1.25] break-all">
                    {g.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2 text-center text-[10px] sm:text-[11px] text-slate-500">
          本页面由 <span className="font-semibold text-amber-700">轩辕国学AI</span> 生成，仅供民俗参考，相信科学
        </div>
      </div>

      {showThemeSettings && (
        <div className="fixed inset-0 z-[72] bg-black/35 p-4" onClick={() => setShowThemeSettings(false)}>
          <div
            className={`ml-auto mt-[calc(env(safe-area-inset-top,0px)+52px)] w-[220px] rounded-2xl border p-3 shadow-2xl ${
              isModern ? 'border-sky-200 bg-white' : 'border-[#dfc9a9] bg-[#fff8ee]'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">主题样式</span>
              <button
                type="button"
                onClick={() => setShowThemeSettings(false)}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100"
                aria-label="关闭主题设置"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                  onClick={() => applyTheme('warm')}
                  className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                  activeTheme === 'warm'
                    ? 'border-[#c39154] bg-[#fff0dd] text-[#7b4e27]'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                暖雅主题
              </button>
              <button
                type="button"
                  onClick={() => applyTheme('modern')}
                  className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                  activeTheme === 'modern'
                    ? 'border-sky-300 bg-sky-50 text-sky-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                现代主题
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WuxingClothing;
